import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tables } from "@/integrations/supabase/types";
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { toast } from "@/components/ui/sonner";
import { getPackages, createPackage as createPackageService, updatePackage, deletePackage } from '@/services/supabasePackages';
import { 
  normalizePackages, 
  getPackageWithUser, 
  getPackagesArray,
  selectPackageById 
} from '@/lib/utils';

export function usePackages() {
  const queryClient = useQueryClient();
  const { user } = useSupabaseAuth();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['packages'],
    queryFn: async () => {
      try {
        const data = await getPackages();
        return data as Tables<'packages'>[];
      } catch (error: any) {
        toast.error(`Failed to fetch packages: ${error.message}`);
        console.error('Error fetching packages:', error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
    retry: 2,
  });

  const createMutation = useMutation({
    mutationFn: (pkg: Omit<Partial<Tables<'packages'>>, 'id' | 'last_updated_by'>) => {
      toast.info("Creating package...");
      return createPackageService(pkg, user);
    },
    onSuccess: (data, variables, context) => {
        toast.success("Package created successfully");
        queryClient.invalidateQueries({ queryKey: ['packages'] });
    },
    onError: (error: Error) => {
        toast.error(`Creation Failed: ${error.message}`);
    }
  });

  // Normalized data (for new components)
  const normalizedPackages = data ? normalizePackages(data) : {};

  // Helper functions for normalized data access
  const getPackageById = (packageId: number) => selectPackageById(packageId, normalizedPackages);
  const getPackageWithUserData = (packageId: number, users: Record<string, Tables<'profiles'>>) => 
    getPackageWithUser(packageId, normalizedPackages, users);

  return { 
    packages: data, // array format (backward compatible)
    packagesNormalized: normalizedPackages, // normalized format
    isLoading, 
    error, 
    getPackageById,
    getPackageWithUserData,
    getPackagesArray: () => getPackagesArray(normalizedPackages),
    createPackage: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    refetchPackages: refetch
  };
}

export function useUpdatePackage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string, changes: Partial<Tables<'packages'>> }) => updatePackage(vars),
    onMutate: async (variables) => {
      toast.info("Updating package...");
    },
    onSuccess: () => {
        toast.success("Package updated successfully");
        queryClient.invalidateQueries({ queryKey: ['packages'] });
    },
    onError: (error: Error) => {
        toast.error(`Update Failed: ${error.message}`);
    },
  });
}

export function useDeletePackage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePackage(id),
    onMutate: async (variables) => {
      toast.info("Deleting package...");
    },
    onSuccess: () => {
        toast.success("Package deleted successfully");
        queryClient.invalidateQueries({ queryKey: ['packages'] });
    },
    onError: (error: Error) => {
        toast.error(`Deletion Failed: ${error.message}`);
    },
  });
}
